/**
 * ★★ I18N-01 — LA LANGUE, DÉCLARÉE UNE SEULE FOIS POUR TOUT LE PRODUIT.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * CE QUE CE FICHIER DÉCIDE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Le produit est né français et l'est resté jusqu'au 04/09/2026 : la langue
 * n'existait **nulle part** — ni en base, ni dans un DTO, ni sur le locataire.
 * Ce fichier est le point d'entrée unique de son introduction, et il est dans
 * `@tb/shared` pour une raison précise : la langue traverse la frontière
 * back/front dans les deux sens.
 *
 *  · le **backend** en a besoin pour composer un courriel, un billet PDF et
 *    une facture QR — des documents qu'il produit seul, sans navigateur ;
 *  · le **frontend** en a besoin pour tout le reste.
 *
 * Une enum déclarée deux fois est une enum qui divergera : c'est la règle
 * d'ARCHITECTURE §3, §6, la même qui a mis `PHASE_SAISON` et `ROLE` ici.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * ★★ QUI CHOISIT LA LANGUE — arbitrage du 04/09/2026 (Julien)
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Deux porteurs, et deux seulement. C'est une décision, pas un état de fait :
 * deux autres porteurs plausibles ont été **écartés**, et il faut savoir
 * lesquels avant de « corriger » ce qui ressemblerait à un oubli.
 *
 *  1. **LE LOCATAIRE** porte la langue de sa maison. Elle sert de défaut à
 *     tout ce qui le concerne : son back-office, ses pages publiques, et les
 *     documents qu'il émet. Un théâtre bernois configure `de` une fois.
 *
 *  2. **LE VISITEUR** peut la surcharger, mais **sur les pages publiques
 *     seulement** — vitrine, boutique, portail. C'est une préférence
 *     d'affichage, locale à son navigateur ; elle ne modifie jamais une
 *     donnée du théâtre.
 *
 * **Écarté : la langue par utilisateur du back-office.** Le personnel d'une
 * maison travaille dans la langue de la maison. Un réglage par compte
 * imposerait une colonne, un écran de préférences et trois jeux de captures
 * dans la documentation, pour un besoin que personne n'a exprimé.
 *
 * **⚠️ Écarté : la langue par SPECTATEUR.** Conséquence directe et assumée :
 * **un spectateur germanophone chez un théâtre romand reçoit ses billets et sa
 * facture en français.** Ce n'est pas un défaut à corriger discrètement — c'est
 * le prix d'une simplification choisie. La lever demanderait une colonne sur
 * `personne`, un choix à la commande dans la boutique, et les modèles de
 * courriel en trois versions résolues au moment de l'envoi. Le jour où on la
 * lève, c'est `langueDuDocument()` ci-dessous qui change, et lui seul.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI PAS DE BIBLIOTHÈQUE i18n
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `i18next` et consorts résolvent une clé **à l'exécution** et retombent en
 * silence sur la langue de repli quand elle manque. Ce projet tient l'inverse
 * pour une règle : un texte manquant doit **casser la compilation**, comme une
 * exigence non prouvée casse `promesses-vitrine`. Les dictionnaires sont donc
 * des `Traduit<T>` — un `Record` complet sur les trois langues — et TypeScript
 * refuse un allemand oublié. Le coût est un `as const` de plus ; le bénéfice
 * est qu'aucune page ne peut partir en production à moitié traduite.
 */
/* ========================================================================== */
/* 1. LES LANGUES                                                             */
/* ========================================================================== */
/**
 * Les trois langues servies.
 *
 * ★ **L'ordre n'est pas alphabétique, il est éditorial** : c'est celui du
 *   sélecteur, et le français vient en tête parce qu'il est la langue du
 *   produit, de sa documentation et de son éditeur.
 *
 * ⚠️ L'italien manque volontairement. Le marché visé est romand et alémanique ;
 *    ajouter `it` sans traducteur relecteur ne ferait que trois dictionnaires
 *    incomplets de plus. C'est une ligne à ajouter ici le jour où le besoin
 *    existe — et ce jour-là, TypeScript nommera lui-même les 100 % de textes à
 *    écrire.
 */
export const LANGUES = ['fr', 'de', 'en'];
/**
 * La langue servie quand rien n'a été choisi, ni par le locataire ni par le
 * visiteur — et quand une valeur stockée est devenue illisible.
 *
 * Le même raisonnement que `lexique()` : on ne sert jamais une page muette
 * parce qu'une préférence est absente.
 */
export const LANGUE_DEFAUT = 'fr';
/** `true` si la valeur est l'une des trois langues servies. */
export function estLangue(valeur) {
    return typeof valeur === 'string' && LANGUES.includes(valeur);
}
/**
 * Ramène n'importe quoi à une langue servie.
 *
 * ★ Accepte les étiquettes régionales (`de-CH`, `fr-BE`, `en-GB`) en ne
 *   gardant que leur sous-étiquette primaire : c'est la forme que donnent
 *   `navigator.language` et l'en-tête `Accept-Language`, et refuser `de-CH`
 *   servirait du français à toute la Suisse alémanique.
 */
export function versLangue(valeur, defaut = LANGUE_DEFAUT) {
    if (typeof valeur !== 'string')
        return defaut;
    const primaire = valeur.trim().toLowerCase().split(/[-_]/u)[0] ?? '';
    return estLangue(primaire) ? primaire : defaut;
}
/**
 * La langue d'un document émis par un théâtre — billet, facture, courriel.
 *
 * ★★ **Le seul endroit à changer** le jour où la langue du spectateur sera
 *    portée par la personne (voir l'en-tête). Aujourd'hui elle rend la langue
 *    du théâtre, sans condition : c'est la simplification arbitrée, et elle est
 *    écrite ici plutôt que dispersée dans les six modules qui émettent des
 *    documents.
 */
export function langueDuDocument(langueDuTheatre) {
    return langueDuTheatre ?? LANGUE_DEFAUT;
}
/** Prend la valeur d'une langue dans un `Traduit`. */
export function traduire(dictionnaire, langue) {
    return dictionnaire[langue];
}
/* ========================================================================== */
/* 3. LES ÉTIQUETTES DU SÉLECTEUR                                             */
/* ========================================================================== */
/**
 * Le nom de chaque langue **dans cette langue**, jamais traduit.
 *
 * ★ C'est la seule façon correcte d'écrire un sélecteur de langue : quelqu'un
 *   qui cherche l'allemand cherche « Deutsch », pas « allemand ». Afficher les
 *   noms dans la langue courante oblige le lecteur à connaître déjà la langue
 *   qu'il ne comprend pas.
 */
export const NOM_LANGUE = {
    fr: 'Français',
    de: 'Deutsch',
    en: 'English',
};
/**
 * Le code à deux lettres affiché dans un sélecteur compact.
 *
 * ⚠️ `de` et non `ch` : le code est celui de la LANGUE, pas du pays. Un
 *    drapeau suisse ne dirait pas laquelle des quatre langues nationales on
 *    sert, et un drapeau allemand dirait le mauvais pays.
 */
export const CODE_LANGUE = {
    fr: 'FR',
    de: 'DE',
    en: 'EN',
};
/* ========================================================================== */
/* 4. LES FORMATS                                                             */
/* ========================================================================== */
/**
 * L'étiquette BCP 47 à donner à `Intl`, pour les dates et les nombres.
 *
 * ★★ **Toutes les trois sont suisses**, et c'est délibéré. Un théâtre suisse
 *    affiche `04.09.2026` à ses spectateurs alémaniques comme à ses
 *    spectateurs romands ; servir `de-DE` donnerait `4.9.2026` et `en-US`
 *    donnerait `9/4/2026` — une date américaine sur un billet suisse, où le
 *    jour et le mois s'inversent silencieusement pour les douze premiers jours
 *    du mois. C'est exactement le genre de défaut qui ne se voit qu'au
 *    guichet, le soir, quand quelqu'un se présente le mauvais jour.
 *
 * ⚠️ `en-CH` n'est pas une locale officielle mais `Intl` la résout par repli
 *    sur les conventions suisses, ce qui est le comportement voulu. Le fuseau
 *    reste `Europe/Zurich` partout (ARCHITECTURE §3), indépendamment de ceci.
 */
export const LOCALE_INTL = {
    fr: 'fr-CH',
    de: 'de-CH',
    en: 'en-CH',
};
//# sourceMappingURL=langues.js.map